import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  DndContext, useSensor, useSensors, PointerSensor, KeyboardSensor,
  DragOverlay, useDroppable, useDraggable, pointerWithin,
} from '@dnd-kit/core';
import {
  ChevronLeft, ChevronRight, Search, Printer, FileDown, StickyNote, Plus,
  FileText, X, ExternalLink, Users, MoreHorizontal, Download, GripVertical,
} from 'lucide-react';
import { format, addDays, addWeeks, startOfWeek, isSameDay, parseISO, getISOWeek, getISOWeekYear } from 'date-fns';
import { de } from 'date-fns/locale';

import { api, extractError } from '../lib/api';
import { useData } from '../lib/data';
import { useAuth } from '../lib/auth';
import { useConfirm } from '../lib/confirm';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuSeparator, ContextMenuTrigger,
} from '../components/ui/context-menu';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { EmployeeChip } from '../components/EmployeeChip';
import { EmployeePalette } from '../components/EmployeePalette';
import {
  AssignEmployeeDialog, LendToSchabosDialog, LendToOtherKstDialog,
  AddAbsenceDialog, EditAssignmentDialog,
} from '../components/PlantafelDialogs';
import { exportWeeklyPdf } from '../lib/pdfExport';
import { DeviceAssignDialog, DeviceAssignmentChip } from '../components/DeviceAssignDialog';

const WOCHENTAGE = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];

const VIEW_OPTIONS = [
  { key: '1-week', label: '1 Woche', weeks: 1 },
  { key: '2-weeks', label: '2 Wochen', weeks: 2 },
  { key: '4-weeks', label: '4 Wochen', weeks: 4 },
  { key: '8-weeks', label: '8 Wochen', weeks: 8 },
];

export default function PlantafelPage() {
  const navigate = useNavigate();
  const { user, isAdmin, can } = useAuth();
  const canCreate = can('plantafel', 'create');
  const canEditPlan = can('plantafel', 'edit');
  const canDeletePlan = can('plantafel', 'delete');
  const canViewProjects = can('projects', 'view');
  const data = useData();
  const { confirm, prompt } = useConfirm();
  const [viewKey, setViewKey] = useState(() => localStorage.getItem('fd_plantafel_view') || '4-weeks');
  const [rowHeight, setRowHeight] = useState(() => {
    const v = parseInt(localStorage.getItem('fd_plantafel_rowheight') || '', 10);
    return Number.isFinite(v) && v >= 100 ? v : 240;
  });
  const resizeRef = React.useRef(null);
  const startRowResize = (e) => {
    e.preventDefault();
    const startY = e.clientY;
    const startH = rowHeight;
    resizeRef.current = { startY, startH };
    const onMove = (ev) => {
      const d = ev.clientY - resizeRef.current.startY;
      const next = Math.max(100, Math.min(700, resizeRef.current.startH + d));
      setRowHeight(next);
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      setRowHeight(h => { localStorage.setItem('fd_plantafel_rowheight', String(h)); return h; });
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };
  const [anchorDate, setAnchorDate] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const userDefaultKst = user?.cost_center_id || 'all';
  const [kstFilter, setKstFilter] = useState(userDefaultKst);
  const [search, setSearch] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [paletteCollapsed, setPaletteCollapsed] = useState(() => localStorage.getItem('fd_palette_collapsed') === '1');

  // Wenn User eine KSt zugewiesen bekommt, aktualisiere den Default (aber nur initial)
  const lastUserKstRef = React.useRef(user?.cost_center_id);
  useEffect(() => {
    if (user?.cost_center_id && user.cost_center_id !== lastUserKstRef.current) {
      setKstFilter(user.cost_center_id);
      lastUserKstRef.current = user.cost_center_id;
    }
  }, [user?.cost_center_id]);

  const [assignDialog, setAssignDialog] = useState({ open: false, date: '', empId: '', projId: '', clusterIndex: 0 });
  const [schabosDialog, setSchabosDialog] = useState({ open: false, empId: '', date: '' });
  const [kstLendDialog, setKstLendDialog] = useState({ open: false, empId: '', date: '' });
  const [absenceDialog, setAbsenceDialog] = useState({ open: false, empId: '', date: '' });
  const [editDialog, setEditDialog] = useState({ open: false, assignment: null });
  const [noteDialog, setNoteDialog] = useState({ open: false, date: '', text: '', kstId: null });
  const [clusterNoteDialog, setClusterNoteDialog] = useState({ open: false, date: '', projectId: '', clusterIndex: 0, projectName: '', text: '' });
  const [deviceDialog, setDeviceDialog] = useState({ open: false });
  const [activeDrag, setActiveDrag] = useState(null);
  // Angeklickter Tag. Die Palette ("Frei …") bezieht sich darauf, nicht mehr
  // starr auf heute – sonst kann man einen künftigen Tag nicht besetzen, ohne
  // im Kopf abzugleichen, wer an dem Tag schon verplant ist.
  const [fokusDatum, setFokusDatum] = useState(null);

  const viewCfg = VIEW_OPTIONS.find(v => v.key === viewKey) || VIEW_OPTIONS[2];
  // Sonntag wird normalerweise nicht gearbeitet und würde nur Platz kosten.
  // Er muss aber sichtbar gemacht werden können: Schulungen und Azubi-Termine
  // landen gelegentlich am Sonntag, und ein Eintrag, den man nicht sieht, ist
  // schlimmer als eine Spalte zu viel – man sucht ihn dann in der falschen
  // Woche. Die Wahl bleibt gespeichert.
  // Zusammengefasst zu einem Schalter für Samstag UND Sonntag. Vorher war der
  // Samstag fest eingeblendet und kostete eine volle Spalte, obwohl dort meist
  // nichts steht – fünf Werktage auf dem Bildschirm heißt mehr Breite je Tag
  // und damit lesbarere Einsätze.
  const [wochenendeZeigen, setWochenendeZeigen] = useState(() => (
    localStorage.getItem('fd_plantafel_wochenende') === '1'
    // Alter Schlüssel: Wer den Sonntag schon eingeschaltet hatte, wollte das
    // Wochenende sehen. Die Einstellung soll durch die Umbenennung nicht
    // stillschweigend zurückgehen.
    || localStorage.getItem('fd_plantafel_sonntag') === '1'
  ));
  const tageProWoche = wochenendeZeigen ? 7 : 5;
  const weeks = useMemo(
    () => buildWeeksFrom(anchorDate, viewCfg.weeks, tageProWoche),
    [anchorDate, viewCfg.weeks, tageProWoche]
  );
  const fromDate = format(weeks[0].days[0], 'yyyy-MM-dd');
  const toDate = format(weeks[weeks.length - 1].days[tageProWoche - 1], 'yyyy-MM-dd');

  useEffect(() => { localStorage.setItem('fd_plantafel_view', viewKey); }, [viewKey]);
  useEffect(() => { localStorage.setItem('fd_plantafel_wochenende', wochenendeZeigen ? '1' : '0'); }, [wochenendeZeigen]);
  useEffect(() => { localStorage.setItem('fd_palette_collapsed', paletteCollapsed ? '1' : '0'); }, [paletteCollapsed]);

  useEffect(() => {
    data.setRange({ from: fromDate, to: toDate });
    data.loadAll(fromDate, toDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate]);

  const empById = useMemo(() => Object.fromEntries(data.employees.map(e => [e.id, e])), [data.employees]);
  const projById = useMemo(() => Object.fromEntries(data.projects.map(p => [p.id, p])), [data.projects]);
  const kstById = useMemo(() => Object.fromEntries(data.costCenters.map(c => [c.id, c])), [data.costCenters]);
  const deviceById = useMemo(() => Object.fromEntries((data.devices || []).map(d => [d.id, d])), [data.devices]);
  // Tagesspezifische Projekt-Reihenfolge (pro Tag unabhängig): key `date::projectId` -> order
  const dayOrderMap = useMemo(() => {
    const m = {};
    for (const o of (data.dayProjectOrder || [])) m[`${o.date}::${o.project_id}`] = o.sort_order;
    return m;
  }, [data.dayProjectOrder]);
  // Über-Stunden-Warnung: EINHEITLICHE Quelle für Mitarbeiter- UND Geräte-Chips.
  // (kombinierte Stunden aus Mitarbeiter- + Geräte-Einsätzen über Soft-Limit, nur heute/Zukunft)
  const overHoursDeviceIds = useMemo(() => {
    const s = new Set();
    for (const o of (data.warnings?.over_hours || [])) {
      for (const id of (o.device_assignment_ids || [])) s.add(id);
    }
    return s;
  }, [data.warnings]);
  const overHoursAssignmentIds = useMemo(() => {
    const s = new Set();
    for (const o of (data.warnings?.over_hours || [])) {
      for (const id of (o.assignment_ids || [])) s.add(id);
    }
    return s;
  }, [data.warnings]);
  // Geräte-Einsätze nach Datum+Projekt gruppieren (für Badges in der normalen Plantafel)
  const deviceAssignmentsByDateProject = useMemo(() => {
    const m = {};
    for (const da of (data.deviceAssignments || [])) {
      if (!da.project_id) continue; // nur Einsätze mit Baustelle erscheinen in der Plantafel
      const key = `${da.date}::${da.project_id}`;
      if (!m[key]) m[key] = [];
      m[key].push(da);
    }
    for (const k in m) m[k].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    return m;
  }, [data.deviceAssignments]);
  const holidayByDate = useMemo(() => {
    const map = {};
    for (const h of data.holidays) { if (h.active !== false) map[h.date] = h; }
    return map;
  }, [data.holidays]);
  const notesByKey = useMemo(() => {
    const m = {};
    for (const n of data.dayNotes) { m[`${n.date}::${n.cost_center_id || 'global'}`] = n; }
    return m;
  }, [data.dayNotes]);
  const clusterNoteByKey = useMemo(() => {
    const m = {};
    // Schlüssel inkl. Block-Index: Jeder Block hat seine eigene Notiz.
    for (const n of (data.clusterNotes || [])) { m[`${n.date}::${n.project_id}::${n.cluster_index || 0}`] = n; }
    return m;
  }, [data.clusterNotes]);

  const doubleBookingIds = useMemo(() => {
    // Konflikt-Markierung für Mitarbeiter-Chips – identische Quelle wie Geräte-Warnungen,
    // damit die Warnhinweise in der Plantafel einheitlich sind.
    return overHoursAssignmentIds;
  }, [overHoursAssignmentIds]);

  const filteredAssignments = useMemo(() => {
    return data.assignments.filter(a => {
      const emp = empById[a.employee_id];
      const proj = projById[a.project_id];
      if (!emp || !proj) return false;
      // Hinweis: Assignments werden IMMER angezeigt, auch wenn MA context='device' hat.
      // Grund: importierte oder alte Einsätze müssen sichtbar bleiben und ggf. korrigiert
      // werden können. Der context-Filter gilt nur für die Palette (draggbare MA-Liste).
      if (kstFilter !== 'all') {
        const homeKst = emp.cost_center_id;
        const projKst = proj.cost_center_id;
        const match = (homeKst === kstFilter) || (projKst === kstFilter);
        if (!match) return false;
      }
      if (projectFilter && a.project_id !== projectFilter) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        if (!`${emp.first_name} ${emp.last_name}`.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [data.assignments, kstFilter, projectFilter, search, empById, projById]);

  // Group assignments per date, then cluster by project
  const clustersByDate = useMemo(() => {
    // Gruppiert wird nach Projekt UND Block (cluster_index). Für normale
    // Projekte ist der Index immer 0, dort ändert sich dadurch nichts.
    // Sammelprojekte wie 0005 Reparatur können an einem Tag mehrere Blöcke
    // haben – getrennte Aufträge mit eigener Besetzung und eigener Notiz.
    const blockSchluessel = (projectId, idx) => `${projectId}::${idx || 0}`;
    const m = {};
    for (const a of filteredAssignments) {
      const k = blockSchluessel(a.project_id, a.cluster_index);
      if (!m[a.date]) m[a.date] = {};
      if (!m[a.date][k]) m[a.date][k] = [];
      m[a.date][k].push(a);
    }
    // Geräte-Einsätze berücksichtigen: sorgen dafür, dass ein Cluster existiert,
    // auch wenn nur ein Gerät (ohne Mitarbeiter-Einsatz) an dem Tag auf der Baustelle ist.
    const searchActive = !!search.trim();
    if (!searchActive) {
      for (const da of (data.deviceAssignments || [])) {
        if (!da.project_id) continue;
        const proj = projById[da.project_id];
        if (!proj) continue;
        if (projectFilter && da.project_id !== projectFilter) continue;
        if (kstFilter !== 'all' && proj.cost_center_id !== kstFilter) continue;
        // Geräte hängen immer am ersten Block (Index 0). Ein Gerät einem
        // bestimmten Reparatur-Auftrag zuzuordnen wäre denkbar, braucht aber
        // ein eigenes Feld am Geräte-Einsatz – solange das nicht existiert,
        // ist eine falsche Zuordnung schlimmer als eine pauschale.
        const k = blockSchluessel(da.project_id, 0);
        if (!m[da.date]) m[da.date] = {};
        if (!m[da.date][k]) m[da.date][k] = [];
      }
    }
    const result = {};
    for (const d in m) {
      const clusters = Object.entries(m[d]).map(([blockKey, items]) => {
        const [projectId, idxRaw] = blockKey.split('::');
        const clusterIndex = Number(idxRaw) || 0;
        const proj = projById[projectId];
        const projectKst = proj ? kstById[proj.cost_center_id] : null;
        const totalHours = items.reduce((sum, x) => sum + (typeof x.hours === 'number' ? x.hours : 8), 0);
        // Geräte nur am ersten Block anzeigen, siehe oben.
        const deviceItems = clusterIndex === 0
          ? (deviceAssignmentsByDateProject[`${d}::${projectId}`] || [])
          : [];
        return {
          projectId, clusterIndex, blockKey, project: proj, projectKst,
          totalHours,
          deviceItems,
          items: items.sort((x, y) => {
            const so = (x.sort_order ?? 0) - (y.sort_order ?? 0);
            if (so !== 0) return so;
            const ex = empById[x.employee_id];
            const ey = empById[y.employee_id];
            return `${ex?.first_name} ${ex?.last_name}`.localeCompare(`${ey?.first_name} ${ey?.last_name}`);
          }),
        };
      });
      clusters.sort((a, b) => {
        const rank = (proj) => {
          if (!proj) return 99999;
          const dv = dayOrderMap[`${d}::${proj.id}`];
          return dv !== undefined ? dv : (1000 + (proj.sort_order ?? 0));
        };
        const ra = rank(a.project);
        const rb = rank(b.project);
        if (ra !== rb) return ra - rb;
        const na = a.project?.project_number || a.project?.name || '';
        const nb = b.project?.project_number || b.project?.name || '';
        const nc = na.localeCompare(nb);
        if (nc !== 0) return nc;
        // Blöcke desselben Projekts bleiben beieinander, in ihrer Reihenfolge.
        return (a.clusterIndex || 0) - (b.clusterIndex || 0);
      });
      result[d] = clusters;
    }
    return result;
  }, [filteredAssignments, projById, kstById, empById, data.deviceAssignments, deviceAssignmentsByDateProject, projectFilter, kstFilter, search, dayOrderMap]);

  // GESAMT-Stunden aller Assignments pro Projekt (systemweit) – kommt aus neuem Endpoint /api/projects-hours-summary
  const projectHoursSummary = data.projectHoursSummary || {};
  const projectTotalHours = useMemo(() => {
    const m = {};
    for (const pid in projectHoursSummary) {
      m[pid] = projectHoursSummary[pid]?.planned_hours ?? 0;
    }
    return m;
  }, [projectHoursSummary]);

  // Pro-MA-Pro-Tag Stundensumme (für Über-8h Highlighting)
  const empDayHours = useMemo(() => {
    const m = {};
    for (const a of data.assignments) {
      const k = `${a.employee_id}::${a.date}`;
      m[k] = (m[k] || 0) + (typeof a.hours === 'number' ? a.hours : 8);
    }
    return m;
  }, [data.assignments]);

  // Absences for display (abwesende MA)
  const absencesByEmpDay = useMemo(() => {
    const m = {};
    for (const a of data.absences) { m[`${a.employee_id}::${a.date}`] = a; }
    return m;
  }, [data.absences]);

  const absentEmployeesByDate = useMemo(() => {
    const m = {};
    for (const ab of data.absences) {
      if (ab.half_day) continue;
      const emp = empById[ab.employee_id];
      if (!emp || !emp.active) continue;
      if (kstFilter !== 'all' && emp.cost_center_id !== kstFilter) continue;
      if (search.trim() && !`${emp.first_name} ${emp.last_name}`.toLowerCase().includes(search.toLowerCase())) continue;
      if (!m[ab.date]) m[ab.date] = [];
      m[ab.date].push({ absence: ab, employee: emp });
    }
    return m;
  }, [data.absences, empById, kstFilter, search]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor),
  );

  const toggleSelection = useCallback((assignmentId, shift) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (shift) {
        if (next.has(assignmentId)) next.delete(assignmentId);
        else next.add(assignmentId);
      } else {
        if (next.has(assignmentId) && next.size === 1) next.clear();
        else { next.clear(); next.add(assignmentId); }
      }
      return next;
    });
  }, []);

  const clearSelection = () => setSelectedIds(new Set());

  // Über welchem Element hängt der Zeiger gerade? Nur fürs Anzeigen der
  // Einfügestelle beim Verschieben eines Projekt-Clusters. Ohne diese Anzeige
  // sieht man beim Ziehen nicht, wo das Cluster landet – man lässt los und
  // schaut dann, was passiert ist.
  const [dragOverId, setDragOverId] = useState(null);

  const handleDragStart = (e) => {
    const id = e.active.id;
    setActiveDrag(id);
    if (typeof id === 'string' && !id.startsWith('palette::') && !id.startsWith('devicedrag::') && !id.startsWith('clusterdrag::') && !selectedIds.has(id)) {
      setSelectedIds(new Set([id]));
    }
  };

  const handleDragEnd = async (e) => {
    const dragId = e.active?.id;
    const overId = e.over?.id;
    setActiveDrag(null);
    setDragOverId(null);
    if (!overId || !dragId) return;

    // Ganzes Projekt-Cluster: gleicher Tag = Reihenfolge ändern, anderer Tag = verschieben
    if (typeof dragId === 'string' && dragId.startsWith('clusterdrag::')) {
      const parts = dragId.split('::');
      const srcDate = parts[1];
      const projId = parts[2];
      let newDate = null;
      let overCluster = overId;
      // Falls über einem Chip losgelassen: dessen Cluster (Datum+Projekt) verwenden
      if (typeof overId === 'string' && overId.startsWith('chipdrop::')) {
        const t = data.assignments.find(a => a.id === overId.replace('chipdrop::', ''));
        if (t) overCluster = `cluster::${t.date}::${t.project_id}::${t.cluster_index || 0}`;
      }
      if (overCluster.startsWith('cluster::')) {
        const op = overCluster.split('::');
        const tDate = op[1];
        const tProj = op[2];
        if (tDate === srcDate) {
          // Reihenfolge der Projekt-Cluster NUR an diesem Tag ändern (unabhängig von anderen Tagen)
          if (tProj && tProj !== projId) await reorderDayClusters(srcDate, projId, tProj);
          return;
        }
        newDate = tDate;
      } else if (overCluster.startsWith('cell::')) {
        newDate = overCluster.replace('cell::', '');
      } else {
        return;
      }
      if (!newDate || newDate === srcDate) return;
      // NUR den gezogenen Block. Vorher fehlte die Einschränkung auf den
      // Block – bei einem Sammelprojekt wanderten dadurch alle Reparaturen
      // des Tages mit, obwohl nur eine gezogen wurde.
      const srcIndex = Number(parts[3]) || 0;
      const items = data.assignments.filter(a =>
        a.project_id === projId && a.date === srcDate && (a.cluster_index || 0) === srcIndex);
      if (items.length === 0) return;
      const zielIndex = zielBlockFuerVerschieben(projId, newDate, srcIndex);
      const moves = items.map(a => ({
        assignment_id: a.id, new_date: newDate, new_cluster_index: zielIndex,
      }));
      try {
        const r = await api.post('/assignments/bulk-move', { moves });
        const ok = r.data.succeeded?.length || 0;
        const fail = r.data.failed?.length || 0;
        if (fail === 0) toast.success(`Projekt-Cluster (${ok}) auf ${newDate} verschoben.`);
        else toast.warning(`${ok} verschoben, ${fail} abgelehnt`, { description: (r.data.failed || []).map(f => f.reason).join('\n') });
        await data.refresh();
      } catch (err) { toast.error(extractError(err)); }
      return;
    }

    // Drop auf Papierkorb der Palette → Einsatz löschen (nur für bestehende Chips, nicht Palette-MA)
    if (overId === 'palette-trash') {
      if (typeof dragId === 'string' && dragId.startsWith('palette::')) return; // Palette-MA in Palette droppen = nix
      // Geräte-Badge in Papierkorb → Geräte-Einsatz löschen (verschwindet auch aus der Geräte-Plantafel)
      if (typeof dragId === 'string' && dragId.startsWith('devicedrag::')) {
        const daId = dragId.replace('devicedrag::', '');
        try {
          await api.delete(`/device-assignments/${daId}`);
          toast.success('Geräte-Einsatz entfernt (auch aus der Geräte-Plantafel).');
          await data.refresh();
        } catch (err) { toast.error(extractError(err)); }
        return;
      }
      // Multi-select: Wenn dragId Teil der Auswahl → alle löschen
      const idsToRemove = Array.from(selectedIds.size > 0 && selectedIds.has(dragId) ? selectedIds : new Set([dragId]));
      try {
        await Promise.all(idsToRemove.map(id => api.delete(`/assignments/${id}`)));
        toast.success(`${idsToRemove.length} Einsatz/Einsätze entfernt.`);
        clearSelection();
        await data.refresh();
      } catch (err) { toast.error(extractError(err)); }
      return;
    }

    // Geräte-Badge verschieben: auf leere Zelle (nur Tag ändern) oder auf Cluster (Tag + Projekt ändern)
    if (typeof dragId === 'string' && dragId.startsWith('devicedrag::')) {
      const daId = dragId.replace('devicedrag::', '');
      const da = (data.deviceAssignments || []).find(x => x.id === daId);
      if (!da) return;
      let payload = null;
      if (overId.startsWith('cluster::')) {
        const op = overId.split('::');
        payload = { date: op[1], project_id: op[2] };
      } else if (overId.startsWith('cell::')) {
        payload = { date: overId.replace('cell::', '') };
      } else {
        return;
      }
      if (payload.date === da.date && (payload.project_id === undefined || payload.project_id === da.project_id)) return;
      try {
        await api.patch(`/device-assignments/${daId}`, payload);
        toast.success('Geräte-Einsatz verschoben.');
        await data.refresh();
      } catch (err) { toast.error(extractError(err)); }
      return;
    }

    // Palette-Drop: auf Cluster ODER auf leere Zelle
    if (typeof dragId === 'string' && dragId.startsWith('palette::')) {
      const empId = dragId.replace('palette::', '');

      // Case 1a: Drop direkt auf einen bestehenden Projekt-Cluster → direkter Einsatz, kein Dialog
      if (overId.startsWith('cluster::')) {
        const parts = overId.split('::');
        const targetDate = parts[1];
        const targetProjectId = parts[2];
        // Der Einsatz landet in genau dem Block, auf den gezogen wurde.
        const targetIndex = Number(parts[3]) || 0;
        // Check abwesend
        const ab = absencesByEmpDay[`${empId}::${targetDate}`];
        if (ab && !ab.half_day) {
          const emp = empById[empId];
          toast.error(`${emp?.first_name} ${emp?.last_name} ist an diesem Tag abwesend (${ab.type}).`);
          return;
        }
        // Sammelprojekt mit bereits vorhandenem Block: nachfragen statt raten.
        const zielProjekt = projById[targetProjectId];
        if (zielProjekt?.allow_multiple_per_day) {
          setBlockFrage({
            empId,
            date: targetDate,
            projectId: targetProjectId,
            projektName: zielProjekt.name,
            projektNummer: zielProjekt.project_number,
            zielBlock: targetIndex,
            neuerBlock: naechsterBlock(targetProjectId, targetDate),
          });
          return;
        }
        await einsatzAnlegen(empId, targetDate, targetProjectId, targetIndex);
        return;
      }

      // Case 1b: Drop auf leere Zelle → Dialog öffnen zur Projektauswahl
      if (overId.startsWith('cell::')) {
        const newDate = overId.replace('cell::', '');
        const ab = absencesByEmpDay[`${empId}::${newDate}`];
        if (ab && !ab.half_day) {
          const emp = empById[empId];
          toast.error(`${emp?.first_name} ${emp?.last_name} ist an diesem Tag abwesend (${ab.type}).`);
          return;
        }
        setAssignDialog({ open: true, date: newDate, empId, projId: '', clusterIndex: 0 });
      }
      return;
    }

    // Case 2: Bestehender Chip verschoben
    if (!overId.startsWith('cell::') && !overId.startsWith('cluster::')) return;
    let newDate;
    if (overId.startsWith('cluster::')) {
      // Drop auf Cluster: verschiebt auf das Datum, Projekt wird optional getauscht wenn Admin
      const parts = overId.split('::');
      newDate = parts[1];
      const targetProjectId = parts[2];
      const targetIndex = Number(parts[3]) || 0;
      // If single chip dragged (no multi-select), also update project
      const dragged = data.assignments.find(a => a.id === dragId);
      if (!dragged) return;
      const movingIds = Array.from(selectedIds.size > 0 && selectedIds.has(dragId) ? selectedIds : new Set([dragId]));
      if (movingIds.length === 1) {
        try {
          const payload = { date: newDate };
          if (dragged.project_id !== targetProjectId) payload.project_id = targetProjectId;
          // Auch der Block muss mitgeschrieben werden – sonst springt der
          // Einsatz beim Ziehen zwischen zwei Reparatur-Blöcken wieder zurück.
          if ((dragged.cluster_index || 0) !== targetIndex) payload.cluster_index = targetIndex;
          await api.patch(`/assignments/${dragId}`, payload);
          toast.success('Einsatz verschoben.');
          clearSelection();
          await data.refresh();
        } catch (err) { toast.error(extractError(err)); }
        return;
      }
      // Multi-move to date (keep projects)
    } else {
      newDate = overId.replace('cell::', '');
    }

    const movingIds = Array.from(selectedIds.size > 0 ? selectedIds : new Set([dragId]));
    const dragged = data.assignments.find(a => a.id === dragId);
    if (!dragged) return;
    const delta = dayDiff(dragged.date, newDate);
    const movesAll = movingIds.map(aid => {
      const a = data.assignments.find(x => x.id === aid);
      if (!a) return null;
      const zielDatum = addDaysIso(a.date, delta);
      return {
        assignment_id: aid,
        new_date: zielDatum,
        // Auch bei einzelnen Chips: Die Blocknummer gilt pro Tag. Am Zieltag
        // könnte dieselbe Nummer zu einem anderen Auftrag gehören.
        new_cluster_index: zielBlockFuerVerschieben(a.project_id, zielDatum, a.cluster_index || 0),
      };
    }).filter(Boolean);
    try {
      const resp = await api.post('/assignments/bulk-move', { moves: movesAll });
      const okCount = resp.data.succeeded.length;
      const failCount = resp.data.failed.length;
      if (failCount === 0) toast.success(`${okCount} Einsatz/Einsätze verschoben.`);
      else {
        const details = resp.data.failed.map(f => f.reason).join('\n');
        toast.warning(`${okCount} verschoben, ${failCount} abgelehnt`, { description: details });
      }
      clearSelection();
      await data.refresh();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const removeAssignment = async (assignmentId) => {
    try {
      await api.delete(`/assignments/${assignmentId}`);
      toast.success('Einsatz entfernt.');
      setSelectedIds(prev => { const n = new Set(prev); n.delete(assignmentId); return n; });
      await data.refresh();
    } catch (err) { toast.error(extractError(err)); }
  };

  const removeSelected = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    const ok = await confirm({
      title: `${ids.length} Einsatz/Einsätze entfernen?`,
      description: 'Diese Einsätze werden unwiderruflich aus der Plantafel entfernt.',
      variant: 'destructive',
      confirmLabel: 'Entfernen',
    });
    if (!ok) return;
    for (const id of ids) {
      try { await api.delete(`/assignments/${id}`); } catch {}
    }
    toast.success(`${ids.length} Einsatz/Einsätze entfernt.`);
    clearSelection();
    await data.refresh();
  };

  // Cluster-Aktionen ---------------------------------------------------------
  const handleMoveCluster = async (cluster, dateIso, dayOffset) => {
    let targetDate;
    if (dayOffset === null) {
      const val = await prompt({
        title: 'Cluster auf Datum verschieben',
        description: `Auf welches Datum soll das Cluster "${cluster.project?.name}" verschoben werden?`,
        label: 'Zieldatum',
        type: 'date',
        defaultValue: addDaysIso(dateIso, 1),
        confirmLabel: 'Verschieben',
      });
      if (!val) return;
      if (!/^\d{4}-\d{2}-\d{2}$/.test(val)) { toast.error('Ungültiges Datum.'); return; }
      targetDate = val;
    } else {
      targetDate = addDaysIso(dateIso, dayOffset);
    }
    const zielIndex = zielBlockFuerVerschieben(cluster.projectId, targetDate, cluster.clusterIndex || 0);
    const moves = cluster.items.map(a => ({
      assignment_id: a.id, new_date: targetDate, new_cluster_index: zielIndex,
    }));
    try {
      const r = await api.post('/assignments/bulk-move', { moves });
      const ok = r.data.succeeded?.length || 0;
      const fail = r.data.failed?.length || 0;
      if (fail === 0) toast.success(`${ok} Einsatz/Einsätze auf ${targetDate} verschoben.`);
      else toast.warning(`${ok} verschoben, ${fail} abgelehnt`, { description: (r.data.failed || []).map(f => f.reason).join('\n') });
      await data.refresh();
    } catch (err) { toast.error(extractError(err)); }
  };

  const handleDuplicateCluster = async (cluster, dateIso, dayOffset) => {
    // dayOffset === null → nach dem Zieldatum fragen. Gleiche Bedienung wie
    // beim Verschieben, damit man sich nur eine Regel merken muss.
    let targetDate;
    if (dayOffset === null) {
      const val = await prompt({
        title: 'Cluster auf Datum kopieren',
        description: `Auf welches Datum soll „${cluster.project?.name}" kopiert werden? `
          + `Der Einsatz am ${dateIso} bleibt bestehen.`,
        label: 'Zieldatum',
        type: 'date',
        defaultValue: addDaysIso(dateIso, 1),
        confirmLabel: 'Kopieren',
      });
      if (!val) return;
      if (!/^\d{4}-\d{2}-\d{2}$/.test(val)) { toast.error('Ungültiges Datum.'); return; }
      targetDate = val;
    } else {
      targetDate = addDaysIso(dateIso, dayOffset);
    }
    const kopieIndex = zielBlockFuerVerschieben(cluster.projectId, targetDate, cluster.clusterIndex || 0);
    try {
      for (const a of cluster.items) {
        await api.post('/assignments', {
          employee_id: a.employee_id, project_id: a.project_id, date: targetDate, hours: a.hours ?? 8,
          // Blocknummer am Zieltag bestimmen, nicht blind übernehmen: Sonst
          // landet die Kopie in einem dort schon bestehenden fremden Auftrag.
          cluster_index: kopieIndex,
        });
      }
      toast.success(`Cluster nach ${targetDate} kopiert (${cluster.items.length} Einsätze).`);
      await data.refresh();
    } catch (err) { toast.error(extractError(err)); }
  };

  const handleDeleteCluster = async (cluster, dateIso) => {
    const ok = await confirm({
      title: `Cluster "${cluster.project?.name}" löschen?`,
      description: `Alle ${cluster.items.length} Einsätze in diesem Cluster am ${dateIso} werden entfernt.`,
      variant: 'destructive',
      confirmLabel: 'Löschen',
    });
    if (!ok) return;
    try {
      await Promise.all(cluster.items.map(a => api.delete(`/assignments/${a.id}`)));
      toast.success(`${cluster.items.length} Einsatz/Einsätze entfernt.`);
      await data.refresh();
    } catch (err) { toast.error(extractError(err)); }
  };
  // -------------------------------------------------------------------------

  /**
   * Alle Projekte eines Tages in der aktuell gültigen Reihenfolge – ohne
   * Rücksicht auf Filter.
   *
   * Nötig, weil es pro Tag nur EINE gespeicherte Reihenfolge gibt. Wer mit
   * aktivem Kostenstellen-Filter umsortiert, sieht aber nur einen Teil. Würde
   * man nur diesen Teil mit 0…n neu durchnummerieren, bekämen die
   * ausgeblendeten Projekte dieselben Nummern – und die Reihenfolge wäre
   * danach eine andere als die, die man eingestellt hat. Deshalb wird immer
   * die vollständige Liste geschrieben und das verschobene Projekt nur darin
   * umgehängt.
   */
  const vollstaendigeTagesreihenfolge = (iso) => {
    const ids = new Set();
    for (const a of (data.assignments || [])) {
      if (a.date === iso && a.project_id) ids.add(a.project_id);
    }
    for (const da of (data.deviceAssignments || [])) {
      if (da.date === iso && da.project_id) ids.add(da.project_id);
    }
    const rang = (pid) => {
      const dv = dayOrderMap[`${iso}::${pid}`];
      if (dv !== undefined) return dv;
      return 1000 + (projById[pid]?.sort_order ?? 0);
    };
    const name = (pid) => {
      const p = projById[pid];
      return p?.project_number || p?.name || '';
    };
    return [...ids].sort((x, y) => {
      const r = rang(x) - rang(y);
      if (r !== 0) return r;
      return name(x).localeCompare(name(y));
    });
  };

  // Projekt-Cluster-Reihenfolge NUR für einen bestimmten Tag ändern (unabhängig von anderen Tagen).
  const reorderDayClusters = async (date, projId, targetProjId) => {
    const ordered = vollstaendigeTagesreihenfolge(date);
    const from = ordered.indexOf(projId);
    const target = ordered.indexOf(targetProjId);
    if (from < 0 || target < 0) return;
    ordered.splice(from, 1);
    // Richtung berücksichtigen: nach unten ziehen -> hinter Ziel, nach oben ziehen -> vor Ziel
    const newTargetIdx = ordered.indexOf(targetProjId);
    const insertIdx = from < target ? newTargetIdx + 1 : newTargetIdx;
    ordered.splice(insertIdx, 0, projId);
    try {
      await api.post('/day-project-order/reorder', { date, ordered_project_ids: ordered });
      toast.success('Reihenfolge für diesen Tag angepasst.');
      await data.refresh();
    } catch (err) { toast.error(extractError(err)); }
  };

  /**
   * Wo landet das Cluster, das gerade gezogen wird – vor oder hinter diesem?
   *
   * Die Logik muss genau der von reorderDayClusters entsprechen: nach unten
   * gezogen wird hinter dem Ziel eingefügt, nach oben davor. Eine Anzeige, die
   * etwas anderes verspricht als danach passiert, ist schlimmer als keine.
   */
  const einfuegeHinweis = (iso, dayClusters, projectId) => {
    if (typeof activeDrag !== 'string' || !activeDrag.startsWith('clusterdrag::')) return null;
    const [, srcDate, srcProj] = activeDrag.split('::');
    // Anderer Tag = das Cluster wird verschoben, nicht umsortiert.
    if (srcDate !== iso) return null;
    if (!dragOverId || typeof dragOverId !== 'string') return null;

    let overKey = dragOverId;
    if (overKey.startsWith('chipdrop::')) {
      const t = data.assignments.find(a => a.id === overKey.replace('chipdrop::', ''));
      if (t) overKey = `cluster::${t.date}::${t.project_id}::${t.cluster_index || 0}`;
    }
    if (!overKey.startsWith('cluster::')) return null;
    const [, oDate, oProj] = overKey.split('::');
    if (oDate !== iso || oProj !== projectId || oProj === srcProj) return null;

    const from = dayClusters.findIndex(c => c.projectId === srcProj);
    const to = dayClusters.findIndex(c => c.projectId === oProj);
    if (from < 0 || to < 0) return null;
    return from < to ? 'after' : 'before';
  };

  /**
   * Weiteren Block desselben Projekts an diesem Tag anlegen.
   *
   * Ein Block besteht aus seinen Einsätzen – ohne einen einzigen Mitarbeiter
   * gibt es nichts anzuzeigen. Deshalb wird nicht "leer angelegt", sondern der
   * Zuweisen-Dialog mit dem Projekt und dem nächsten freien Block geöffnet.
   */
  /** Welche Blöcke eines Projekts gibt es an diesem Tag schon? Aufsteigend. */
  const bloeckeFuer = (projectId, iso) => {
    const idx = new Set();
    for (const a of (data.assignments || [])) {
      if (a.date === iso && a.project_id === projectId) idx.add(a.cluster_index || 0);
    }
    return [...idx].sort((a, b) => a - b);
  };

  /**
   * Welche Blocknummer bekommt ein Block am Zieltag?
   *
   * Blocknummern gelten pro Tag. Ist die eigene Nummer am Zieltag noch frei,
   * bleibt sie – das hält die Reihenfolge nachvollziehbar. Ist sie belegt,
   * würde der Block mit einem fremden Auftrag verschmelzen; dann die nächste
   * freie. Bei Projekten ohne Mehrfach-Freigabe bleibt es immer bei 0.
   */
  const zielBlockFuerVerschieben = (projectId, zielDatum, quellIndex) => {
    if (!projById[projectId]?.allow_multiple_per_day) return 0;
    const belegt = new Set(bloeckeFuer(projectId, zielDatum));
    if (!belegt.has(quellIndex)) return quellIndex;
    let n = 0;
    while (belegt.has(n)) n += 1;
    return n;
  };

  /** Nächste freie Blocknummer für Projekt + Tag. */
  const naechsterBlock = (projectId, iso) => {
    const belegt = new Set(bloeckeFuer(projectId, iso));
    let n = 0;
    while (belegt.has(n)) n += 1;
    return n;
  };

  const neuenBlockAnlegen = (cluster, iso) => {
    setAssignDialog({
      open: true, date: iso, empId: '',
      projId: cluster.projectId, clusterIndex: naechsterBlock(cluster.projectId, iso),
    });
  };

  /**
   * Rückfrage beim Ziehen auf ein Sammelprojekt.
   *
   * Zieht man jemanden auf einen bestehenden 0005-Block, ist nicht
   * entscheidbar, was gemeint ist: zweiter Mann auf derselben Reparatur, oder
   * eine weitere Reparatur. Vorher wurde stillschweigend das Erste getan –
   * und die zweite Reparatur gab es nicht, weil niemand das Pluszeichen fand.
   * Also wird gefragt, und zwar nur hier, wo die Frage auch entsteht.
   */
  const [blockFrage, setBlockFrage] = useState(null);

  const einsatzAnlegen = async (empId, iso, projectId, clusterIndex) => {
    try {
      await api.post('/assignments', {
        employee_id: empId, project_id: projectId, date: iso,
        cluster_index: clusterIndex || 0,
      });
      toast.success(clusterIndex ? `Einsatz im Block #${clusterIndex + 1} angelegt.` : 'Einsatz angelegt.');
      await data.refresh();
    } catch (err) { toast.error(extractError(err)); }
  };

  const openDeviceEdit = (da) => {
    const dev = deviceById[da.device_id];
    setDeviceDialog({
      open: true,
      assignment: da,
      deviceId: da.device_id,
      date: da.date,
      defaultProjectId: da.project_id || null,
      defaultDriverId: da.driver_employee_id || null,
      defaultHours: da.hours ?? 8,
      defaultNotes: da.notes || '',
      _deviceName: dev?.name,
    });
  };

  const openDeviceCreate = (date, projectId) => {
    setDeviceDialog({
      open: true,
      assignment: null,
      deviceId: null,          // -> Geräte-Auswahl im Dialog
      date,
      defaultProjectId: projectId || null,
      defaultDriverId: null,
      defaultHours: 8,
      defaultNotes: '',
    });
  };

  // Die Notiz gehört zu der Kostenstelle, die gerade gefiltert ist. Steht der
  // Filter auf "alle", wird es eine allgemeine Notiz für den ganzen Tag.
  const openNoteEditor = (date, kstId = undefined) => {
    const ziel = kstId !== undefined ? kstId : (kstFilter === 'all' ? null : kstFilter);
    const existing = notesByKey[`${date}::${ziel || 'global'}`];
    setNoteDialog({ open: true, date, text: existing?.text || '', kstId: ziel });
  };

  const saveNote = async () => {
    try {
      await api.put('/day-notes', { date: noteDialog.date, cost_center_id: noteDialog.kstId || null, text: noteDialog.text });
      toast.success('Notiz gespeichert.');
      setNoteDialog({ open: false, date: '', text: '', kstId: null });
      await data.refresh();
    } catch (err) { toast.error(extractError(err)); }
  };

  const openClusterNoteEditor = (date, projectId, projectName, clusterIndex = 0) => {
    const existing = clusterNoteByKey[`${date}::${projectId}::${clusterIndex || 0}`];
    setClusterNoteDialog({
      open: true, date, projectId, clusterIndex: clusterIndex || 0,
      projectName: projectName || '', text: existing?.text || '',
    });
  };
  const saveClusterNote = async () => {
    try {
      await api.put('/cluster-notes', {
        date: clusterNoteDialog.date, project_id: clusterNoteDialog.projectId,
        cluster_index: clusterNoteDialog.clusterIndex || 0, text: clusterNoteDialog.text,
      });
      toast.success('Cluster-Notiz gespeichert.');
      setClusterNoteDialog({ open: false, date: '', projectId: '', clusterIndex: 0, projectName: '', text: '' });
      await data.refresh();
    } catch (err) { toast.error(extractError(err)); }
  };

  const exportCsv = async () => {
    try {
      const resp = await api.get('/export/week-csv', {
        params: { from_date: fromDate, to_date: toDate },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Einsaetze_${fromDate}_${toDate}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(extractError(err)); }
  };

  const exportPdf = () => {
    exportWeeklyPdf({
      weeks, assignments: filteredAssignments, employees: data.employees,
      projects: data.projects, costCenters: data.costCenters, holidays: holidayByDate,
      filename: `Einsaetze_${fromDate}_${toDate}.pdf`,
    });
  };

  const gotoPrev = () => setAnchorDate(prev => addWeeks(prev, -viewCfg.weeks));
  const gotoNext = () => setAnchorDate(prev => addWeeks(prev, viewCfg.weeks));
  const gotoToday = () => setAnchorDate(startOfWeek(new Date(), { weekStartsOn: 1 }));

  const today = new Date();

  const handleProjectClick = (project) => {
    // Herkunft mitgeben, damit "Zurück" wieder hierher führt und nicht in
    // die Projektliste. Wer aus der Plantafel heraus ein Projekt aufschlägt,
    // will danach weiterplanen – und verliert sonst Woche, Filter und
    // Scrollposition.
    if (canViewProjects && project?.id) {
      navigate(`/projekte/${project.id}`, { state: { herkunft: '/plantafel' } });
    }
  };

  return (
    <div className="flex h-screen">
      {/* Main Plantafel */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Toolbar - kompakt: Navigation + Filter + Ansicht + KSt-Tabs in einer Zeile */}
        <div className="no-print flex items-center gap-1.5 px-3 py-1.5 border-b border-slate-200 bg-white flex-wrap">
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" onClick={gotoPrev} className="h-7 w-7 p-0" data-testid="plantafel-prev"><ChevronLeft size={14}/></Button>
            <Button size="sm" variant="outline" onClick={gotoToday} className="h-7 px-2 text-xs" data-testid="plantafel-today">Heute</Button>
            <Button size="sm" variant="outline" onClick={gotoNext} className="h-7 w-7 p-0" data-testid="plantafel-next"><ChevronRight size={14}/></Button>
          </div>
          <div className="text-xs font-semibold font-mono text-slate-700 px-1">
            {format(weeks[0].days[0], 'dd.MM.', { locale: de })} – {format(weeks[weeks.length-1].days[tageProWoche-1], 'dd.MM.yyyy', { locale: de })}
          </div>
          <Select value={viewKey} onValueChange={setViewKey}>
            <SelectTrigger className="w-[100px] h-7 text-xs" data-testid="plantafel-view-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {VIEW_OPTIONS.map(v => <SelectItem key={v.key} value={v.key}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>

          {/* Standardmäßig aus – sonst nimmt eine praktisch immer leere Spalte
              ein Siebtel der Breite weg. Angeschaltet bleibt die Wahl
              gespeichert, auch über das Schließen des Browsers hinaus. */}
          <Button
            size="sm"
            variant={wochenendeZeigen ? 'default' : 'outline'}
            onClick={() => setWochenendeZeigen(v => !v)}
            className="h-7 px-2 text-xs"
            title={wochenendeZeigen
              ? 'Samstag und Sonntag ausblenden – mehr Platz für die Werktage'
              : 'Samstag und Sonntag einblenden – nötig für Wochenendeinsätze, Schulungen und Termine'}
            data-testid="plantafel-wochenende-toggle"
          >
            {wochenendeZeigen ? 'Wochenende aus' : 'Wochenende anzeigen'}
          </Button>

          <div className="h-5 w-px bg-slate-200 mx-0.5" />

          {/* KSt-Filter inline (kompakt) */}
          <div className="flex items-center gap-0.5" data-testid="plantafel-costcenter-tabs">
            <button
              onClick={() => setKstFilter('all')}
              className={`text-[11px] font-semibold px-2 py-1 rounded h-7 border ${kstFilter === 'all' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'}`}
              data-testid="plantafel-kst-all"
            >
              Alle
            </button>
            {data.costCenters.filter(c => !c.deleted && c.active).map(c => (
              <button
                key={c.id}
                onClick={() => setKstFilter(c.id)}
                className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded h-7 border ${kstFilter === c.id ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'}`}
                data-testid={`plantafel-kst-${c.short_code}`}
              >
                <span className="w-2 h-2 rounded-sm" style={{ background: c.color }}></span>
                {c.short_code}
                {user?.cost_center_id === c.id && <span className="text-[8px] text-slate-400 ml-0.5">(meine)</span>}
              </button>
            ))}
          </div>

          <div className="h-5 w-px bg-slate-200 mx-0.5" />

          <div className="relative w-40">
            <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"/>
            <Input placeholder="Mitarbeiter" className="pl-6 h-7 text-xs" value={search} onChange={e => setSearch(e.target.value)} data-testid="plantafel-employee-search-input"/>
            {search && <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-slate-400 hover:text-slate-900"><X size={10}/></button>}
          </div>
          <Select value={projectFilter || '__all__'} onValueChange={(v) => setProjectFilter(v === '__all__' ? '' : v)}>
            <SelectTrigger className="w-[160px] h-7 text-xs" data-testid="plantafel-project-filter"><SelectValue placeholder="Projekt filtern"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Alle Projekte</SelectItem>
              {data.projects.filter(p => p.status === 'Aktiv').map(p => (
                <SelectItem key={p.id} value={p.id}>{p.project_number ? `#${p.project_number} ` : ''}{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="ml-auto flex items-center gap-1">
            {selectedIds.size > 0 && (
              <>
                <Badge className="bg-slate-900 text-white text-[10px] px-1.5" data-testid="plantafel-selection-count">{selectedIds.size}×</Badge>
                {canDeletePlan && <Button size="sm" variant="destructive" onClick={removeSelected} className="h-7 text-xs" data-testid="plantafel-remove-selected">Entfernen</Button>}
                <Button size="sm" variant="outline" onClick={clearSelection} className="h-7 text-xs">Auswahl ×</Button>
              </>
            )}
            <Button size="sm" variant="outline" onClick={exportCsv} className="h-7 text-xs hidden" data-testid="plantafel-export-csv-hidden" aria-hidden="true"><FileText size={12} className="mr-1"/>CSV</Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="plantafel-export-menu">
                  <Download size={12} className="mr-1"/>Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={exportPdf} data-testid="plantafel-export-pdf">
                  <FileDown size={14} className="mr-2"/>Als PDF exportieren
                </DropdownMenuItem>
                <DropdownMenuItem onClick={exportCsv} data-testid="plantafel-export-csv">
                  <FileText size={14} className="mr-2"/>Als CSV exportieren
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => window.print()} data-testid="plantafel-print">
                  <Printer size={14} className="mr-2"/>Drucken (A4 quer)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <DndContext
          sensors={sensors}
          collisionDetection={pointerWithin}
          onDragStart={handleDragStart}
          onDragOver={(e) => setDragOverId(e.over?.id || null)}
          onDragEnd={handleDragEnd}
          onDragCancel={() => { setActiveDrag(null); setDragOverId(null); }}
        >
          <div className="flex-1 flex min-h-0">
            <div className="flex-1 overflow-auto scroll-thin bg-white">
              <div className="min-w-[900px]">
                {/* Header row */}
                <div className={`plantafel-grid ${wochenendeZeigen ? 'mit-wochenende' : ''} sticky top-0 z-10 bg-[#eef2f7] border-b border-slate-300`}>
                  <div className="h-8 flex items-center justify-center text-[10px] font-semibold text-slate-600 font-mono border-r border-slate-300">KW</div>
                  {WOCHENTAGE.slice(0, tageProWoche).map(d => (
                    <div key={d} className="h-8 flex items-center justify-center text-[10px] font-semibold text-slate-700 uppercase tracking-wider border-r border-slate-200 last:border-r-0">{d}</div>
                  ))}
                </div>

                {weeks.map((w, wIdx) => (
                  <React.Fragment key={wIdx}>
                  <div className={`plantafel-grid ${wochenendeZeigen ? 'mit-wochenende' : ''}`}>
                    <div className="bg-[#eef2f7] border-r border-slate-300 flex flex-col items-center justify-center py-1 text-slate-700">
                      <div className="text-[10px] font-mono font-semibold">KW</div>
                      <div className="text-sm font-bold font-display leading-tight">{w.isoWeek}</div>
                      <div className="text-[9px] text-slate-500 font-mono mt-0.5">{format(w.days[0], 'dd.MM', { locale: de })}</div>
                    </div>
                    {w.days.map((day, dIdx) => {
                      const iso = format(day, 'yyyy-MM-dd');
                      const holiday = holidayByDate[iso];
                      // Samstag und Sonntag gleich einfärben – beides keine
                      // Regelarbeitstage.
                      const isSat = dIdx >= 5;
                      const isToday = isSameDay(day, today);
                      const dayClusters = clustersByDate[iso] || [];
                      const dayAbsences = absentEmployeesByDate[iso] || [];
                      // Bei aktivem Filter die Notiz dieser Kostenstelle zeigen,
                      // sonst die allgemeine. Steht der Filter auf "alle",
                      // wird zusätzlich angezeigt, dass es Notizen einzelner
                      // Kostenstellen gibt – sonst sähe es aus, als wäre die
                      // eben geschriebene Notiz verschwunden.
                      const dayNote = kstFilter === 'all'
                        ? notesByKey[`${iso}::global`]
                        : (notesByKey[`${iso}::${kstFilter}`] || notesByKey[`${iso}::global`]);
                      const alleNotizen = (data.dayNotes || []).filter(n => n.date === iso && (n.text || '').trim());
                      const notizTitel = alleNotizen
                        .map(n => `${n.cost_center_id ? (kstById[n.cost_center_id]?.short_code || 'KSt') : 'Alle'}: ${n.text}`)
                        .join('\n');
                      return (
                        <DayCell
                          key={iso}
                          dateIso={iso}
                          date={day}
                          holiday={holiday}
                          isSaturday={isSat}
                          isToday={isToday}
                          note={dayNote || (alleNotizen.length ? { text: notizTitel } : null)}
                          noteTitle={notizTitel}
                          noteCount={alleNotizen.length}
                          onOpenNote={() => openNoteEditor(iso)}
                          onClickEmpty={() => { if (canCreate) setAssignDialog({ open: true, date: iso, empId: '', projId: '', clusterIndex: 0 }); }}
                          canEdit={canCreate}
                          contentHeight={rowHeight}
                          imFokus={fokusDatum === iso}
                          onFokus={setFokusDatum}
                        >
                          {dayClusters.map((cluster, cIdx) => (
                            <ProjectCluster
                              // Schlüssel MUSS den Block enthalten. Mit nur der
                              // Projekt-ID bekamen zwei Blöcke desselben Projekts
                              // an einem Tag denselben Schlüssel – React hält sie
                              // dann für dasselbe Element, behält einen DOM-Knoten
                              // und mischt die Daten beider Blöcke. Daher die
                              // scheinbaren Duplikate und die Blöcke, die sich
                              // nicht mehr bearbeiten oder löschen ließen.
                              key={cluster.blockKey}
                              cluster={cluster}
                              dateIso={iso}
                              position={cIdx + 1}
                              dropHint={einfuegeHinweis(iso, dayClusters, cluster.projectId)}
                              // Hat dieser Tag eine eigene Reihenfolge, oder gilt
                              // noch die allgemeine aus der Projektliste?
                              eigeneTagesreihenfolge={dayOrderMap[`${iso}::${cluster.projectId}`] !== undefined}
                              selectedIds={selectedIds}
                              doubleBookingIds={doubleBookingIds}
                              empById={empById}
                              kstById={kstById}
                              projectTotalHours={projectTotalHours[cluster.projectId] || 0}
                              onToggleSelect={toggleSelection}
                              onEdit={(a) => setEditDialog({ open: true, assignment: a })}
                              onRemove={removeAssignment}
                              onLendSchabos={(a) => setSchabosDialog({ open: true, empId: a.employee_id, date: a.date })}
                              onLendKst={(a) => setKstLendDialog({ open: true, empId: a.employee_id, date: a.date })}
                              onAddAbsence={(a) => setAbsenceDialog({ open: true, empId: a.employee_id, date: a.date })}
                              onProjectClick={handleProjectClick}
                              onMoveCluster={handleMoveCluster}
                              onDuplicateCluster={handleDuplicateCluster}
                              onDeleteCluster={handleDeleteCluster}
                              deviceById={deviceById}
                              onDeviceClick={openDeviceEdit}
                              overHoursDeviceIds={overHoursDeviceIds}
                              onNeuerBlock={canCreate ? neuenBlockAnlegen : undefined}
                              clusterNote={clusterNoteByKey[`${iso}::${cluster.projectId}::${cluster.clusterIndex || 0}`]}
                              onOpenClusterNote={() => openClusterNoteEditor(iso, cluster.projectId, cluster.project?.name, cluster.clusterIndex || 0)}
                              onAddDevice={can('device_plantafel', 'create') ? openDeviceCreate : undefined}
                              canEdit={canEditPlan || canCreate}
                            />
                          ))}
                          {dayAbsences.map(({ absence, employee: ae }) => {
                            const homeKst = kstById[ae.cost_center_id];
                            return (
                              <EmployeeChip
                                key={absence.id}
                                assignment={{ id: `abs-${absence.id}` }}
                                employee={ae}
                                project={null}
                                costCenter={homeKst}
                                projectCostCenter={null}
                                isAbsent={true}
                                absenceType={absence.type}
                                compact
                                onContextMenu={(e) => e.preventDefault()}
                              />
                            );
                          })}
                        </DayCell>
                      );
                    })}
                  </div>
                  <div
                    onPointerDown={startRowResize}
                    className="h-2 mb-1 cursor-row-resize flex items-center justify-center group"
                    title="Zeilenhöhe ziehen"
                    data-testid={`row-resize-${wIdx}`}
                  >
                    <div className="w-16 h-1 rounded-full bg-slate-300 group-hover:bg-slate-500 transition-colors" />
                  </div>
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Employee Palette */}
            {canCreate && (
              <EmployeePalette
                employees={data.employees}
                costCenters={data.costCenters}
                assignments={data.assignments}
                absences={data.absences}
                todayIso={fokusDatum || format(today, 'yyyy-MM-dd')}
                istHeute={!fokusDatum || fokusDatum === format(today, 'yyyy-MM-dd')}
                collapsed={paletteCollapsed}
                onToggleCollapsed={() => setPaletteCollapsed(v => !v)}
                activeDrag={activeDrag}
              />
            )}
          </div>

          <DragOverlay>
            {activeDrag ? (() => {
              if (typeof activeDrag === 'string' && activeDrag.startsWith('palette::')) {
                const empId = activeDrag.replace('palette::', '');
                const emp = empById[empId];
                if (!emp) return null;
                const kst = kstById[emp.cost_center_id];
                return (
                  <EmployeeChip
                    assignment={{ id: 'drag-preview' }}
                    employee={emp}
                    project={null}
                    costCenter={kst}
                    projectCostCenter={null}
                    compact
                    dragging
                    style={{ boxShadow: '0 10px 30px rgba(0,0,0,0.25)', transform: 'rotate(1deg)', minWidth: 180 }}
                  />
                );
              }
              if (typeof activeDrag === 'string' && activeDrag.startsWith('devicedrag::')) {
                const daId = activeDrag.replace('devicedrag::', '');
                const da = (data.deviceAssignments || []).find(x => x.id === daId);
                const dev = da ? deviceById[da.device_id] : null;
                return (
                  <div className="px-2 py-1 rounded bg-orange-500 text-white text-xs font-bold shadow-2xl" style={{ transform: 'rotate(1deg)' }}>
                    {dev?.name || 'Gerät'} verschieben…
                  </div>
                );
              }
              if (typeof activeDrag === 'string' && activeDrag.startsWith('clusterdrag::')) {
                // Vorschau in Clustergröße statt als kleine Pille: Beim
                // Verschieben muss man sehen, WAS man in der Hand hat – wie
                // viele Leute und welches Projekt. Eine Beschriftung von
                // zwei Zentimetern Breite sagt das nicht, und man trifft den
                // Zieltag schlechter, wenn das Gezogene kaum sichtbar ist.
                const [, dIso, projId, idxRaw] = activeDrag.split('::');
                const bIdx = Number(idxRaw) || 0;
                const cl = (clustersByDate[dIso] || [])
                  .find(c => c.projectId === projId && (c.clusterIndex || 0) === bIdx);
                const proj = projById[projId];
                const kst = proj ? kstById[proj.cost_center_id] : null;
                const farbe = kst?.color || '#64748b';
                return (
                  <div
                    className="rounded shadow-2xl overflow-hidden bg-white border"
                    style={{
                      width: 210,
                      borderColor: farbe,
                      transform: 'rotate(1deg) scale(0.96)',
                      opacity: 0.95,
                    }}
                  >
                    <div className="px-2 py-1 text-[11px] font-bold text-white flex items-center gap-1.5"
                         style={{ background: farbe }}>
                      <span className="font-mono">{proj?.project_number || ''}</span>
                      <span className="truncate">{proj?.name || 'Projekt'}</span>
                      {bIdx > 0 && <span className="font-mono">#{bIdx + 1}</span>}
                      <span className="ml-auto bg-black/25 rounded px-1">{cl?.items?.length ?? 0}</span>
                    </div>
                    <div className="px-2 py-1 space-y-0.5">
                      {(cl?.items || []).slice(0, 6).map(it => {
                        const e = empById[it.employee_id];
                        return (
                          <div key={it.id} className="text-[11px] text-slate-700 truncate">
                            {e ? `${e.first_name} ${e.last_name}` : '—'}
                          </div>
                        );
                      })}
                      {(cl?.items?.length || 0) > 6 && (
                        <div className="text-[10px] text-slate-500">
                          + {cl.items.length - 6} weitere
                        </div>
                      )}
                      {(cl?.items?.length || 0) === 0 && (
                        <div className="text-[10px] text-slate-400">nur Geräte</div>
                      )}
                    </div>
                  </div>
                );
              }
              const a = data.assignments.find(x => x.id === activeDrag);
              if (!a) return null;
              const emp = empById[a.employee_id];
              const proj = projById[a.project_id];
              const kst = kstById[emp?.cost_center_id];
              const projectKst = kstById[proj?.cost_center_id];
              const count = selectedIds.size > 1 ? selectedIds.size : 1;
              return (
                <div className="relative">
                  <EmployeeChip
                    assignment={a}
                    employee={emp}
                    project={proj}
                    costCenter={kst}
                    projectCostCenter={projectKst}
                    compact
                    dragging
                    style={{ boxShadow: '0 10px 30px rgba(0,0,0,0.25)', transform: 'rotate(1deg)', minWidth: 180 }}
                  />
                  {count > 1 && (
                    <span className="absolute -top-2 -right-2 bg-slate-900 text-white text-[10px] font-bold rounded-full w-6 h-6 flex items-center justify-center">+{count - 1}</span>
                  )}
                </div>
              );
            })() : null}
          </DragOverlay>
        </DndContext>
      </div>

      {/* Dialogs */}
      <AssignEmployeeDialog
        open={assignDialog.open} onOpenChange={(v) => setAssignDialog({ ...assignDialog, open: v })}
        defaultDate={assignDialog.date} defaultEmpId={assignDialog.empId}
        defaultProjId={assignDialog.projId} clusterIndex={assignDialog.clusterIndex}
        bloeckeFuer={bloeckeFuer}
        employees={data.employees} projects={data.projects} costCenters={data.costCenters}
        onAssigned={() => data.refresh()}
      />
      <LendToSchabosDialog
        open={schabosDialog.open} onOpenChange={(v) => setSchabosDialog({ ...schabosDialog, open: v })}
        defaultEmpId={schabosDialog.empId} defaultDate={schabosDialog.date}
        onLent={() => data.refresh()}
      />
      <LendToOtherKstDialog
        open={kstLendDialog.open} onOpenChange={(v) => setKstLendDialog({ ...kstLendDialog, open: v })}
        defaultEmpId={kstLendDialog.empId} defaultDate={kstLendDialog.date}
        employees={data.employees} projects={data.projects} costCenters={data.costCenters}
        onLent={() => data.refresh()}
      />
      <AddAbsenceDialog
        open={absenceDialog.open} onOpenChange={(v) => setAbsenceDialog({ ...absenceDialog, open: v })}
        defaultEmpId={absenceDialog.empId} defaultDate={absenceDialog.date}
        employees={data.employees}
        onAdded={() => data.refresh()}
      />
      <EditAssignmentDialog
        open={editDialog.open} onOpenChange={(v) => setEditDialog({ ...editDialog, open: v })}
        assignment={editDialog.assignment} projects={data.projects} costCenters={data.costCenters}
        onUpdated={() => data.refresh()}
      />

      <DeviceAssignDialog
        state={deviceDialog}
        onClose={() => setDeviceDialog({ ...deviceDialog, open: false })}
        employees={data.employees}
        projects={data.projects}
        absences={data.absences}
        devices={data.devices || []}
        onSaved={() => data.refresh()}
        canEdit={can('device_plantafel', 'edit') || can('device_plantafel', 'create')}
        canDelete={can('device_plantafel', 'delete')}
      />

      {/* Note Dialog */}
      {noteDialog.open && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4" onClick={() => setNoteDialog({ ...noteDialog, open: false })}>
          <div className="bg-white rounded-lg shadow-lg p-5 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="text-sm font-semibold font-display mb-2">Notiz zum {format(parseISO(noteDialog.date), 'dd.MM.yyyy', { locale: de })}</div>
            <div className="mb-3">
              <div className="text-xs font-semibold text-slate-600 mb-1">Gilt für</div>
              <select
                className="w-full h-9 rounded-md border border-slate-200 px-2 text-sm bg-white"
                value={noteDialog.kstId || 'global'}
                onChange={e => {
                  const wert = e.target.value === 'global' ? null : e.target.value;
                  const vorhanden = notesByKey[`${noteDialog.date}::${wert || 'global'}`];
                  setNoteDialog({ ...noteDialog, kstId: wert, text: vorhanden?.text || '' });
                }}
                data-testid="day-note-kst"
              >
                <option value="global">Alle Kostenstellen</option>
                {data.costCenters.filter(c => c.active && !c.deleted).map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <p className="text-[11px] text-slate-500 mt-1">
                Eine Notiz pro Kostenstelle und Tag. Sie erscheint auch in der Tagesansicht.
              </p>
            </div>
            <Textarea value={noteDialog.text} onChange={e => setNoteDialog({ ...noteDialog, text: e.target.value })} rows={5} data-testid="day-note-textarea"/>
            <div className="flex justify-end gap-2 mt-3">
              <Button variant="outline" size="sm" onClick={() => setNoteDialog({ ...noteDialog, open: false })}>Abbrechen</Button>
              {canCreate && <Button size="sm" onClick={saveNote} data-testid="day-note-save">Speichern</Button>}
            </div>
          </div>
        </div>
      )}
      {/* Rückfrage bei Sammelprojekten: derselbe Auftrag oder ein weiterer?
          Drei Möglichkeiten statt Bestätigen/Abbrechen, weil beide Antworten
          etwas tun und keine die naheliegendere ist. Klick daneben oder Escape
          bricht ab – ohne Zuordnung passiert nichts. */}
      {blockFrage && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4"
             onClick={() => setBlockFrage(null)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-4" onClick={e => e.stopPropagation()}
               data-testid="block-frage-dialog">
            <div className="font-display font-bold text-slate-900">
              {empById[blockFrage.empId]
                ? `${empById[blockFrage.empId].first_name} ${empById[blockFrage.empId].last_name}`
                : 'Mitarbeiter'} einplanen
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              {blockFrage.projektNummer ? `${blockFrage.projektNummer} · ` : ''}{blockFrage.projektName}
              {' · '}{format(parseISO(blockFrage.date), 'dd.MM.yyyy', { locale: de })}
            </div>
            <p className="text-sm text-slate-600 mt-3">
              Dieses Projekt kann an einem Tag mehrere getrennte Aufträge haben.
              Gehört der Einsatz zum bestehenden Block oder ist es ein weiterer Auftrag?
            </p>
            <div className="mt-4 space-y-2">
              <Button
                className="w-full justify-start"
                variant="outline"
                onClick={() => { const f = blockFrage; setBlockFrage(null); einsatzAnlegen(f.empId, f.date, f.projectId, f.zielBlock); }}
                data-testid="block-frage-bestehend"
              >
                In den bestehenden Block
                <span className="ml-1 text-slate-500">
                  (#{(blockFrage.zielBlock || 0) + 1}, {bloeckeFuer(blockFrage.projectId, blockFrage.date).length === 1 ? 'der einzige' : 'der angeklickte'})
                </span>
              </Button>
              <Button
                className="w-full justify-start"
                onClick={() => { const f = blockFrage; setBlockFrage(null); einsatzAnlegen(f.empId, f.date, f.projectId, f.neuerBlock); }}
                data-testid="block-frage-neu"
              >
                Weiterer Auftrag – neuer Block #{(blockFrage.neuerBlock || 0) + 1}
              </Button>
            </div>
            <div className="flex justify-end mt-3">
              <Button variant="ghost" size="sm" onClick={() => setBlockFrage(null)}>Abbrechen</Button>
            </div>
          </div>
        </div>
      )}

      {clusterNoteDialog.open && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4" onClick={() => setClusterNoteDialog({ ...clusterNoteDialog, open: false })}>
          <div className="bg-white rounded-lg shadow-lg p-5 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="text-sm font-semibold font-display mb-1">Cluster-Notiz</div>
            <div className="text-xs text-slate-500 mb-2">{clusterNoteDialog.projectName} · {format(parseISO(clusterNoteDialog.date), 'dd.MM.yyyy', { locale: de })}</div>
            <Textarea value={clusterNoteDialog.text} onChange={e => setClusterNoteDialog({ ...clusterNoteDialog, text: e.target.value })} rows={4} placeholder="Notiz für dieses Projekt an diesem Tag…" data-testid="cluster-note-textarea"/>
            <div className="flex justify-end gap-2 mt-3">
              <Button variant="outline" size="sm" onClick={() => setClusterNoteDialog({ ...clusterNoteDialog, open: false })}>Abbrechen</Button>
              <Button size="sm" onClick={saveClusterNote} data-testid="cluster-note-save">Speichern</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function buildWeeksFrom(anchorDate, numberOfWeeks, tageProWoche = 6) {
  const result = [];
  const firstMonday = startOfWeek(anchorDate, { weekStartsOn: 1 });
  for (let i = 0; i < numberOfWeeks; i++) {
    const monday = addWeeks(firstMonday, i);
    const days = [];
    for (let d = 0; d < tageProWoche; d++) days.push(addDays(monday, d));
    result.push({ isoWeek: getISOWeek(monday), isoYear: getISOWeekYear(monday), monday, days });
  }
  return result;
}

function dayDiff(a, b) {
  return Math.round((parseISO(b).getTime() - parseISO(a).getTime()) / 86400000);
}

function addDaysIso(iso, delta) {
  const d = parseISO(iso);
  const n = addDays(d, delta);
  return format(n, 'yyyy-MM-dd');
}

function DayCell({ dateIso, date, holiday, isSaturday, isToday, note, noteTitle, noteCount = 0, children, onOpenNote, onClickEmpty, canEdit, contentHeight = 240, imFokus, onFokus }) {
  const { setNodeRef, isOver } = useDroppable({ id: `cell::${dateIso}` });
  const cellCls = [
    'plantafel-cell p-1',
    holiday ? 'holiday' : '',
    isSaturday ? 'saturday' : '',
    isToday ? 'today' : '',
    isOver ? 'drop-target' : '',
    imFokus ? 'im-fokus' : '',
  ].join(' ');
  return (
    // onMouseDown statt onClick: greift auch, wenn der Klick an einem Chip
    // oder Knopf hängen bleibt, und nimmt keinem Element sein Verhalten weg.
    <div ref={setNodeRef} className={cellCls} onMouseDown={() => onFokus?.(dateIso)}
         data-testid={`plantafel-cell-${dateIso}`}>
      <div className="flex items-center justify-between mb-0.5 gap-1">
        <div className="flex items-center gap-1 min-w-0">
          <span className="text-[10px] font-mono font-semibold text-slate-600">{format(date, 'dd.MM.', { locale: de })}</span>
          {holiday && <span className="text-[8px] font-bold bg-amber-600 text-white rounded px-1 truncate max-w-[110px]" title={holiday.name}>{holiday.name}</span>}
        </div>
        <div className="flex items-center gap-0.5">
          {note && (
            <span className="text-[10px] bg-amber-100 text-amber-900 px-1 rounded cursor-pointer"
                  title={noteTitle || note.text} onClick={onOpenNote}>
              📝{noteCount > 1 ? ` ${noteCount}` : ''}
            </span>
          )}
          {canEdit && <button onClick={onOpenNote} className="p-0.5 text-slate-400 hover:text-slate-900 rounded" title="Notiz"><StickyNote size={10}/></button>}
          {canEdit && <button onClick={onClickEmpty} className="p-0.5 text-slate-400 hover:text-slate-900 rounded" title="Einsatz hinzufügen"><Plus size={11}/></button>}
        </div>
      </div>
      <div className="space-y-0.5 overflow-y-auto scroll-thin" style={{ maxHeight: contentHeight, minHeight: contentHeight }}>
        {children}
      </div>
    </div>
  );
}

/**
 * ProjectCluster - zeigt alle Mitarbeiter eines Projekts gruppiert mit Header.
 * - Cluster ist droppable: Drop aus Palette → direkter Einsatz im Projekt (ohne Dialog)
 * - Cluster-Header ist klickbar → navigiert zur Projekt-Detail-Seite
 * - Rechtsklick auf Header → Cluster-Aktionen (verschieben, kopieren, löschen)
 * - Zeigt Stunden am Tag + Verbrauch/Budget wenn gesetzt
 */
function ProjectCluster({
  cluster, selectedIds, doubleBookingIds, empById, kstById, projectTotalHours = 0,
  onToggleSelect, onEdit, onRemove, onLendSchabos, onLendKst, onAddAbsence, onProjectClick, canEdit,
  onMoveCluster, onDuplicateCluster, onDeleteCluster,
  deviceById = {}, onDeviceClick, onAddDevice, overHoursDeviceIds,
  clusterNote, onOpenClusterNote, onNeuerBlock,
  dateIso, position, dropHint, eigeneTagesreihenfolge,
}) {
  const proj = cluster.project;
  const projectKst = cluster.projectKst;
  const { setNodeRef, isOver } = useDroppable({
    // Vierter Teil = Block-Index. Siehe clustersByDate.
    id: `cluster::${dateIso}::${cluster.projectId}::${cluster.clusterIndex || 0}`,
    data: { type: 'cluster', projectId: cluster.projectId, dateIso },
    disabled: !canEdit,
  });
  const clusterDrag = useDraggable({ id: `clusterdrag::${dateIso}::${cluster.projectId}::${cluster.clusterIndex || 0}`, disabled: !canEdit });
  if (!proj) return null;
  const clusterColor = projectKst?.color || '#64748b';

  const budget = proj.budget_hours;
  const adjustmentsSum = (proj.hour_adjustments || []).reduce((s, x) => s + (x.delta_hours || 0), 0);
  const consumed = projectTotalHours + adjustmentsSum;
  const budgetOverPct = budget && budget > 0 ? Math.round((consumed / budget) * 100) : null;
  const isOverBudget = budget && consumed > budget;
  const budgetLabel = budget && budget > 0
    ? `${Math.round(consumed)}/${Math.round(budget)}h`
    : null;

  const dayTotalLabel = cluster.totalHours && cluster.totalHours !== cluster.items.length * 8
    ? `${Number(cluster.totalHours).toString().replace('.', ',')}h`
    : null;

  const handleHeaderClick = (e) => {
    e.stopPropagation();
    onProjectClick?.(proj);
  };

  return (
    <div
      ref={setNodeRef}
      className={`cluster-wrapper ${isOver ? 'drop-target-cluster' : ''} ${dropHint ? `einfuegen-${dropHint}` : ''} ${canEdit && proj.allow_multiple_per_day ? 'hat-plus' : ''}`}
      style={{
        ['--cluster-accent']: clusterColor,
        ['--cluster-bg']: `${clusterColor}10`,
        ['--cluster-hover']: `${clusterColor}33`,
      }}
      data-testid={`cluster-${dateIso}-${cluster.projectId}-${cluster.clusterIndex || 0}`}
    >
      {/* Einfügemarke: zeigt beim Ziehen, wo das Cluster landet. */}
      {dropHint && <div className={`einfuegemarke einfuegemarke-${dropHint}`} aria-hidden="true" />}
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <button
            type="button"
            onClick={handleHeaderClick}
            className="cluster-header w-full"
            style={{ ['--cluster-accent']: clusterColor }}
            title={`${proj.name}${proj.project_number ? ' · #' + proj.project_number : ''}${budgetLabel ? ' · ' + budgetLabel : ''} – Klick: Projekt öffnen, Rechtsklick: Aktionen`}
            data-testid={`cluster-header-${dateIso}-${cluster.projectId}-${cluster.clusterIndex || 0}`}
          >
            {canEdit && (
              <span
                ref={clusterDrag.setNodeRef}
                {...clusterDrag.listeners}
                {...clusterDrag.attributes}
                onClick={(e) => e.stopPropagation()}
                className="cursor-grab active:cursor-grabbing text-white/60 hover:text-white flex-shrink-0"
                title="Projekt auf anderen Tag ziehen"
                data-testid={`cluster-drag-${dateIso}-${cluster.projectId}-${cluster.clusterIndex || 0}`}
              >
                <GripVertical size={11} />
              </span>
            )}
            {/* Position am Tag. Gezählt wird, was gerade zu sehen ist – ist ein
                Kostenstellen-Filter aktiv, also 1…n innerhalb des Filters.
                Genau das schreibt das Umsortieren auch weg, deshalb ist diese
                Zählung die einzige, die nicht lügt.
                Kräftig = dieser Tag hat eine eigene Reihenfolge. Blass = es
                gilt noch die allgemeine aus der Projektliste. */}
            {position && (
              <span className="cluster-pos"
                    title={eigeneTagesreihenfolge
                      ? `Platz ${position} an diesem Tag (für diesen Tag eigens festgelegt)`
                      : `Platz ${position} – übernommen aus der allgemeinen Projektreihenfolge`}>
                {position}
              </span>
            )}
            {/* Nummer UND Name. Die Nummer allein sagt niemandem etwas, der
                nicht alle Projektnummern im Kopf hat. Die Nummer bleibt
                monospace und vorn, damit die Spalten optisch ausgerichtet
                bleiben; der Name ist das, was abgeschnitten wird, wenn der
                Platz nicht reicht. */}
            <span className="cluster-label">
              {proj.project_number && <span className="cluster-nr">{proj.project_number}</span>}
              <span className="cluster-name">{proj.name}</span>
              {/* Nur ab dem zweiten Block anzeigen – beim einzigen Block eines
                  normalen Projekts wäre eine "1" nur Rauschen. */}
              {(cluster.clusterIndex || 0) > 0 && (
                <span className="cluster-block">#{(cluster.clusterIndex || 0) + 1}</span>
              )}
            </span>
            <span className="cluster-count">{cluster.items.length}</span>
            {dayTotalLabel && (
              <span className="text-[10px] font-mono font-bold text-white/95 ml-0.5 px-1 rounded bg-black/20" title={`Summe an diesem Tag`}>
                {dayTotalLabel}
              </span>
            )}
            {budgetLabel && (
              <span
                className={`text-[10px] font-mono font-bold rounded px-1 ml-0.5 ${isOverBudget ? 'bg-rose-100 text-rose-800' : budgetOverPct >= 80 ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}
                title={`${consumed.toFixed(1).replace('.', ',')}h von ${budget}h (${budgetOverPct}%)`}
                style={{ textShadow: 'none' }}
              >
                {budgetLabel}
              </span>
            )}
            <ExternalLink size={10} className="cluster-link-icon flex-shrink-0" />
          </button>
        </ContextMenuTrigger>
        {/* Sichtbarer Weg zum weiteren Block, direkt im Kopf.
            Im Rechtsklick-Menü allein war die Funktion praktisch nicht
            vorhanden: Dort sucht niemand, der sie nicht schon kennt. Das
            Pluszeichen erscheint nur bei Projekten, die mehrfach pro Tag
            geplant werden dürfen. */}
        {canEdit && proj.allow_multiple_per_day && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onNeuerBlock?.(cluster, dateIso); }}
            className="cluster-plus"
            title={`Weiteren Block für ${proj.name} an diesem Tag anlegen – eigene Besetzung, eigene Notiz`}
            data-testid={`cluster-add-block-${dateIso}-${cluster.projectId}-${cluster.clusterIndex || 0}`}
          >
            <Plus size={11} />
          </button>
        )}
        {canEdit && (
          <ContextMenuContent>
            {/* Nur für Sammelprojekte, die in den Einstellungen dafür
                freigegeben sind (0005 Reparatur, 0008 Wartung). Bei einer
                echten Baustelle wäre ein zweiter Block fast immer ein
                Versehen, deshalb steht der Eintrag dort nicht. */}
            {canEdit && proj.allow_multiple_per_day && (
              <>
                <ContextMenuItem onClick={() => onNeuerBlock?.(cluster, dateIso)}
                                 data-testid={`cluster-new-block-${dateIso}-${cluster.projectId}-${cluster.clusterIndex || 0}`}>
                  Weiteren Block anlegen (eigene Besetzung und Notiz)
                </ContextMenuItem>
                <ContextMenuSeparator />
              </>
            )}
            <ContextMenuItem onClick={() => onMoveCluster?.(cluster, dateIso, 1)} data-testid={`cluster-move-next-${dateIso}-${cluster.projectId}-${cluster.clusterIndex || 0}`}>
              Auf nächsten Werktag verschieben (+1 Tag)
            </ContextMenuItem>
            <ContextMenuItem onClick={() => onMoveCluster?.(cluster, dateIso, 7)}>
              Auf nächste Woche verschieben (+7 Tage)
            </ContextMenuItem>
            <ContextMenuItem onClick={() => onMoveCluster?.(cluster, dateIso, null)}>
              Auf Datum verschieben…
            </ContextMenuItem>
            <ContextMenuSeparator />
            <ContextMenuItem onClick={() => onDuplicateCluster?.(cluster, dateIso, 1)}>
              Auf nächsten Werktag kopieren
            </ContextMenuItem>
            <ContextMenuItem onClick={() => onDuplicateCluster?.(cluster, dateIso, null)}
                             data-testid={`cluster-copy-date-${dateIso}-${cluster.projectId}-${cluster.clusterIndex || 0}`}>
              Auf Datum kopieren…
            </ContextMenuItem>
            <ContextMenuSeparator />
            <ContextMenuItem onClick={() => onOpenClusterNote?.()} data-testid={`cluster-note-edit-${dateIso}-${cluster.projectId}-${cluster.clusterIndex || 0}`}>
              Notiz bearbeiten…
            </ContextMenuItem>
            <ContextMenuSeparator />
            <ContextMenuItem className="text-rose-600" onClick={() => onDeleteCluster?.(cluster, dateIso)} data-testid={`cluster-delete-${dateIso}-${cluster.projectId}-${cluster.clusterIndex || 0}`}>
              Alle Einsätze dieses Clusters löschen
            </ContextMenuItem>
          </ContextMenuContent>
        )}
      </ContextMenu>
      {clusterNote?.text && (
        <button
          type="button"
          onClick={() => onOpenClusterNote?.()}
          className="w-full text-left mt-0.5 px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-[10px] text-amber-900 leading-tight flex items-start gap-1 hover:bg-amber-100"
          title={canEdit ? 'Notiz bearbeiten' : clusterNote.text}
          data-testid={`cluster-note-${dateIso}-${cluster.projectId}-${cluster.clusterIndex || 0}`}
        >
          <StickyNote size={10} className="mt-0.5 flex-shrink-0" />
          <span className="truncate">{clusterNote.text}</span>
        </button>
      )}
      <div className="space-y-0.5 mt-0.5 px-0.5">
        {cluster.items.map((a) => {
          const emp = empById[a.employee_id];
          if (!emp) return null;
          const homeKst = kstById[emp.cost_center_id];
          return (
            <DraggableChip
              key={a.id}
              assignment={a}
              employee={emp}
              project={proj}
              costCenter={homeKst}
              projectCostCenter={projectKst}
              isSelected={selectedIds.has(a.id)}
              isConflict={doubleBookingIds.has(a.id)}
              onToggleSelect={(shift) => onToggleSelect(a.id, shift)}
              onEdit={() => onEdit(a)}
              onRemove={() => onRemove(a.id)}
              onLendSchabos={() => onLendSchabos(a)}
              onLendKst={() => onLendKst(a)}
              onAddAbsence={() => onAddAbsence(a)}
              onProjectClick={onProjectClick}
              canEdit={canEdit}
            />
          );
        })}
      </div>
      {((cluster.deviceItems && cluster.deviceItems.length > 0) || (canEdit && onAddDevice)) && (
        <div className="space-y-0.5 mt-0.5 px-0.5">
          {(cluster.deviceItems || []).map(da => (
            <DraggableDeviceBadge
              key={da.id}
              da={da}
              device={deviceById[da.device_id]}
              project={cluster.project}
              driver={da.driver_employee_id ? empById[da.driver_employee_id] : null}
              defaultDriver={deviceById[da.device_id]?.default_driver_employee_id ? empById[deviceById[da.device_id].default_driver_employee_id] : null}
              canEdit={canEdit}
              onEdit={onDeviceClick ? () => onDeviceClick(da) : undefined}
              warn={overHoursDeviceIds?.has(da.id)}
            />
          ))}
          {canEdit && onAddDevice && (
            <button
              onClick={() => onAddDevice(dateIso, cluster.projectId)}
              className="w-full text-[9px] text-slate-400 hover:text-orange-700 hover:bg-orange-50/50 rounded border border-dashed border-slate-200 py-0.5 flex items-center justify-center gap-1"
              title="Geräte-Einsatz für dieses Projekt anlegen"
              data-testid={`cluster-add-device-${dateIso}-${cluster.projectId}-${cluster.clusterIndex || 0}`}
            >
              <Plus size={9}/>Gerät
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function DraggableDeviceBadge({ da, device, project, driver, defaultDriver, canEdit, onEdit, warn }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `devicedrag::${da.id}`, disabled: !canEdit });
  const dragProps = canEdit ? { ...listeners, ...attributes } : {};
  return (
    <div
      ref={setNodeRef}
      {...dragProps}
      className={canEdit ? 'cursor-grab active:cursor-grabbing' : ''}
      style={{ opacity: isDragging ? 0.4 : 1 }}
      data-testid={`device-drag-${da.id}`}
      title={canEdit ? 'Ziehen zum Verschieben (anderer Tag/Projekt) oder in den Papierkorb • Klick zum Bearbeiten' : undefined}
    >
      <DeviceAssignmentChip
        assignment={da}
        device={device}
        project={project}
        driver={driver}
        defaultDriver={defaultDriver}
        onClick={canEdit && onEdit ? onEdit : undefined}
        compact
        hideProject
        warn={warn}
      />
    </div>
  );
}

function DraggableChip({
  assignment, employee, project, costCenter, projectCostCenter,
  isSelected, isConflict, onToggleSelect, onEdit, onRemove,
  onLendSchabos, onLendKst, onAddAbsence, onProjectClick, canEdit,
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: assignment.id });
  const onPointerDown = (e) => {
    if (e.button !== 0) return;
    onToggleSelect?.(e.shiftKey);
  };
  const chip = (
    <div ref={setNodeRef}>
      <EmployeeChip
        assignment={assignment}
        employee={employee}
        project={project}
        costCenter={costCenter}
        projectCostCenter={projectCostCenter}
        isSelected={isSelected}
        isConflict={isConflict}
        dndAttributes={attributes}
        dndListeners={listeners}
        dragging={isDragging}
        onPointerDown={onPointerDown}
        onProjectClick={onProjectClick}
        compact
      />
    </div>
  );
  if (!canEdit) return chip;
  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{chip}</ContextMenuTrigger>
      <ContextMenuContent data-testid="chip-context-menu">
        <ContextMenuItem onClick={onEdit} data-testid="chip-context-menu-edit">Zuweisung bearbeiten</ContextMenuItem>
        <ContextMenuItem onClick={() => onProjectClick(project)} data-testid="chip-context-menu-project">Projekt öffnen</ContextMenuItem>
        <ContextMenuItem onClick={onAddAbsence} data-testid="chip-context-menu-absence">Abwesenheit eintragen</ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem onClick={onLendKst} data-testid="chip-context-menu-lend-kst">An andere Kostenstelle verleihen</ContextMenuItem>
        <ContextMenuItem onClick={onLendSchabos} data-testid="chip-context-menu-lend-schabos">An Schabos verleihen</ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem onClick={onRemove} className="text-rose-600 focus:text-rose-600" data-testid="chip-context-menu-remove">Zuweisung entfernen</ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}
